import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/ApiProvider';
import type {
  DossierCreateInput,
  DossierListParams,
  DossierTransitionInput,
  DossierUpdateInput,
} from '../../lib/api';

export const dossierKeys = {
  all: ['dossiers'] as const,
  list: (params: DossierListParams) => ['dossiers', 'list', params] as const,
  detail: (id: string) => ['dossiers', 'detail', id] as const,
  history: (id: string) => ['dossiers', 'history', id] as const,
};

export function useDossiers(params: DossierListParams) {
  const api = useApi();
  return useQuery({
    queryKey: dossierKeys.list(params),
    queryFn: () => api.listDossiers(params),
  });
}

export function useDossier(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: dossierKeys.detail(id ?? ''),
    queryFn: () => api.getDossier(id as string),
    enabled: Boolean(id),
  });
}

export function useDossierHistory(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: dossierKeys.history(id ?? ''),
    queryFn: () => api.dossierHistory(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateDossier() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // The Idempotency-Key is generated once per submission attempt and reused on
    // retry, so a network retry never creates a duplicate dossier.
    mutationFn: ({
      input,
      idempotencyKey,
    }: {
      input: DossierCreateInput;
      idempotencyKey: string;
    }) => api.createDossier(input, idempotencyKey),
    onSuccess: () => qc.invalidateQueries({ queryKey: dossierKeys.all }),
  });
}

export function useUpdateDossier(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DossierUpdateInput) => api.updateDossier(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: dossierKeys.all }),
  });
}

export function useTransitionDossier(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DossierTransitionInput) => api.transitionDossier(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: dossierKeys.all }),
  });
}
